import React, { useState } from 'react';
import CheckoutModal from './CheckoutModal';
import { useLanguage } from '../../../../../../../hooks/useLanguage';
import { pos as frPos } from '../../../../../../../locales/fr/pos';
import { pos as esPos } from '../../../../../../../locales/es/pos';
import { pos as arPos } from '../../../../../../../locales/ar/pos';

const translations = {
  fr: frPos,
  es: esPos,
  ar: arPos
};

const Cart = ({ 
  layoutType = 'retail', 
  onClearCart,
  currentCommande = { items: [], montant: { total: 0, tva: 0, ht: 0 } },
  selectedLineIndex,
  onLineSelect,
  onUpdateQuantity,
  onRemoveItem,
  onClearCommande,
  onSaveSale,
  onPaymentComplete
}) => {
  const { lang } = useLanguage();
  const t = translations[lang];

  // Utiliser les montants calculés de currentCommande
  const { total, tva, ht } = currentCommande.montant || { total: 0, tva: 0, ht: 0 };
  const items = currentCommande.items || [];

  // État pour le modal de paiement
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);

  const updateQuantity = (index, newQuantity) => {
    onUpdateQuantity?.(index, newQuantity);
  };

  const removeItem = (index) => {
    onRemoveItem?.(index);
  };

  const clearCart = () => {
    if (window.confirm(t.cart?.clearConfirm || 'Êtes-vous sûr de vouloir vider le panier ?')) {
      onClearCommande?.();
    }
  };

  // Fonction pour ouvrir le modal de paiement
  const handlePaymentClick = () => {
    if (items.length === 0) {
      alert(t.cart?.emptyMessage || 'Le panier est vide. Ajoutez des produits avant de procéder au paiement.');
      return;
    }
    setIsCheckoutModalOpen(true);
  };

  // Fonction pour gérer la finalisation du paiement
  const handlePaymentComplete = (paymentData) => {
    console.log('💳 Paiement finalisé:', paymentData);
    onPaymentComplete?.(paymentData);
    // Vider le panier après paiement réussi
    onClearCommande?.();
  };

  const handleLineClick = (index) => {
    console.log('📝 Sélection ligne', index);
    onLineSelect?.(index);
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border h-full flex flex-col">
        {/* En-tête du panier */}
        <div className="p-4 border-b bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-800">{t.cart?.title || 'Panier'}</h3>
              <span className="text-sm text-gray-500">({items.length} {items.length > 1 ? (t.cart?.itemsPlural || 'articles') : (t.cart?.items || 'article')})</span>
            </div>
            <div className="flex items-center gap-2">
              {items.length > 0 && (
                <>
                  <span className="text-sm font-bold text-green-600">{total.toFixed(2)} €</span>
                  <button 
                    onClick={handlePaymentClick}
                    className="bg-green-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-600 transition-colors"
                  >
                    {t.cart?.pay || 'Payer'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Liste des articles */}
        <div className="flex-1 p-4 overflow-y-auto">
          {items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">🛒</div>
              <p>{t.cart?.empty || 'Panier vide'}</p>
              <p className="text-sm">{t.cart?.emptyMessage || 'Ajoutez des produits pour commencer'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((item, index) => (
                <div 
                  key={index} 
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedLineIndex === index 
                      ? 'bg-blue-100 border-2 border-blue-300' 
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                  onClick={() => handleLineClick(index)}
                >
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm text-gray-800 truncate">{item.name}</h4>
                    <p className="text-xs text-gray-600">{item.unitPrice.toFixed(2)} €</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        updateQuantity(index, item.quantity - 1);
                      }}
                      className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-sm hover:bg-gray-300"
                    >
                      -
                    </button>
                    <span className="text-sm font-medium w-8 text-center">{item.quantity}</span>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        updateQuantity(index, item.quantity + 1);
                      }}
                      className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm hover:bg-blue-600"
                    >
                      +
                    </button>
                  </div>
                  <div className="text-right">
                    <div className="font-medium text-sm">{item.total.toFixed(2)} €</div>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        removeItem(index);
                      }}
                      className="text-red-500 hover:text-red-700 text-lg"
                      title={t.cart?.removeItem || 'Supprimer'}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal de paiement */}
      <CheckoutModal
        isOpen={isCheckoutModalOpen}
        onClose={() => setIsCheckoutModalOpen(false)}
        currentCommande={currentCommande}
        onPaymentComplete={handlePaymentComplete}
        onSaveSale={onSaveSale}
      />
    </>
  );
};

export default Cart; 